import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Trophy, TrendingUp, TrendingDown, Award, Medal, Truck, UserCog } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { useDriverLeaderboard, useVehicleLeaderboard } from '@/hooks/useLeaderboard';

type Subject = 'drivers' | 'vehicles';
type Metric = 'l_per_100km' | 'sar_per_month' | 'sar_per_trip' | 'fills_per_month';

export default function LeaderboardPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [subject, setSubject] = useState<Subject>('drivers');
  const [metric, setMetric] = useState<Metric>('l_per_100km');
  const [periodDays, setPeriodDays] = useState(30);

  const driverLb  = useDriverLeaderboard({ period_days: periodDays, metric, limit: 30 });
  const vehicleLb = useVehicleLeaderboard({ period_days: periodDays, metric, limit: 30 });

  const data = subject === 'drivers' ? driverLb.data : vehicleLb.data;
  const isLoading = subject === 'drivers' ? driverLb.isLoading : vehicleLb.isLoading;
  const fleet = subject === 'drivers' ? driverLb.data?.fleet : null;

  const fmtNum = (n: number | null | undefined, dp = 1) =>
    n == null ? '—' : Number(n).toLocaleString(undefined, { maximumFractionDigits: dp, minimumFractionDigits: dp });
  const fmtSar = (n: number | null | undefined) =>
    n == null ? '—' : Number(n).toLocaleString(undefined, { maximumFractionDigits: 0 }) + ' SAR';

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-card">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard')} className="gap-2">
            <ArrowLeft className="h-4 w-4" />
            {t('common.back')}
          </Button>
          <Trophy className="h-5 w-5 text-amber-500" />
          <h1 className="text-lg font-semibold">{t('leaderboard.title')}</h1>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6 space-y-4">
        {/* Filters */}
        <div className="flex flex-wrap items-center gap-2">
          {/* Subject toggle */}
          <div className="flex border border-border rounded-md overflow-hidden">
            <button
              onClick={() => setSubject('drivers')}
              className={`px-4 py-1.5 text-sm gap-2 inline-flex items-center transition-colors ${
                subject === 'drivers' ? 'bg-gold/20 text-foreground font-medium' : 'bg-card hover:bg-accent'
              }`}>
              <UserCog className="h-4 w-4" />
              {t('leaderboard.drivers')}
            </button>
            <button
              onClick={() => setSubject('vehicles')}
              className={`px-4 py-1.5 text-sm gap-2 inline-flex items-center transition-colors border-l border-border ${
                subject === 'vehicles' ? 'bg-gold/20 text-foreground font-medium' : 'bg-card hover:bg-accent'
              }`}>
              <Truck className="h-4 w-4" />
              {t('leaderboard.vehicles')}
            </button>
          </div>

          {/* Metric */}
          <select
            value={metric}
            onChange={(e) => setMetric(e.target.value as Metric)}
            className="h-9 rounded-md border border-input bg-background px-3 text-sm"
          >
            <option value="l_per_100km">{t('leaderboard.metric.l_per_100km')}</option>
            <option value="sar_per_month">{t('leaderboard.metric.sar_per_month')}</option>
            <option value="sar_per_trip">{t('leaderboard.metric.sar_per_trip')}</option>
            <option value="fills_per_month">{t('leaderboard.metric.fills_per_month')}</option>
          </select>

          {/* Period */}
          <select
            value={periodDays}
            onChange={(e) => setPeriodDays(Number(e.target.value))}
            className="h-9 rounded-md border border-input bg-background px-3 text-sm"
          >
            <option value="7">{t('leaderboard.period.week')}</option>
            <option value="30">{t('leaderboard.period.month')}</option>
            <option value="90">{t('leaderboard.period.quarter')}</option>
            <option value="365">{t('leaderboard.period.year')}</option>
          </select>
        </div>

        {/* Fleet stats banner */}
        {subject === 'drivers' && fleet ? (
          <div className="rounded-md border border-border bg-muted/20 p-4 grid grid-cols-2 md:grid-cols-4 gap-3">
            <FleetStat label={t('leaderboard.fleet.avgL100km')} value={fmtNum(fleet.fleet_avg_l_per_100km)} unit="L/100km" />
            <FleetStat label={t('leaderboard.fleet.totalSar')} value={fmtSar(fleet.fleet_total_sar)} />
            <FleetStat label={t('leaderboard.fleet.totalLiters')} value={fmtNum(fleet.fleet_total_liters, 0)} unit="L" />
            <FleetStat label={t('leaderboard.fleet.totalFills')} value={String(fleet.fleet_fill_count ?? 0)} />
          </div>
        ) : null}

        {/* Top 5 podium */}
        {!isLoading && data?.items?.length ? (
          <div className="grid grid-cols-1 md:grid-cols-5 gap-2">
            {data.items.slice(0, 5).map((entry, i) => (
              <PodiumCard key={`${entry.driver_id ?? entry.vehicle_id}-${i}`} rank={i + 1} entry={entry} subject={subject} metric={metric} />
            ))}
          </div>
        ) : null}

        {/* Full table */}
        {isLoading ? (
          <div className="text-center py-12 text-muted-foreground">{t('common.loading')}</div>
        ) : !data?.items?.length ? (
          <div className="text-center py-16 border border-dashed border-border rounded-md">
            <Trophy className="h-10 w-10 mx-auto text-muted-foreground mb-2" />
            <p className="text-muted-foreground">{t('leaderboard.empty')}</p>
            <p className="text-xs text-muted-foreground mt-1">{t('leaderboard.emptyHint')}</p>
          </div>
        ) : (
          <div className="border border-border rounded-md overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-muted/50">
                <tr>
                  <Th>#</Th>
                  <Th>{t('leaderboard.table.name')}</Th>
                  {subject === 'vehicles' ? <Th>{t('leaderboard.table.brandModel')}</Th> : null}
                  <Th className="text-right">L/100km</Th>
                  <Th className="text-right">{t('leaderboard.table.totalSar')}</Th>
                  <Th className="text-right">{t('leaderboard.table.totalLiters')}</Th>
                  <Th className="text-right">{t('leaderboard.table.fills')}</Th>
                  <Th className="text-right">{t('leaderboard.table.distinct')}</Th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((entry, i) => {
                  const fleetAvg = subject === 'drivers' ? fleet?.fleet_avg_l_per_100km : null;
                  const myL = entry.avg_l_per_100km;
                  const ratio = (fleetAvg && myL) ? Number(myL) / Number(fleetAvg) : null;
                  return (
                    <tr key={`${entry.driver_id ?? entry.vehicle_id}-${i}`} className="border-t border-border hover:bg-accent/50">
                      <Td className="font-mono text-xs">{i + 1}</Td>
                      <Td>
                        <div className="font-medium">
                          {entry.full_name_en ?? entry.plate_en ?? '—'}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {entry.driver_code ?? entry.vehicle_code ?? ''}
                          {entry.usage_role && entry.usage_role !== 'fleet' ? (
                            <Badge variant="inactive" className="ml-1 text-[10px]">{entry.usage_role}</Badge>
                          ) : null}
                        </div>
                      </Td>
                      {subject === 'vehicles' ? (
                        <Td className="text-xs text-muted-foreground">
                          {entry.brand ?? '—'} {entry.model ?? ''}
                        </Td>
                      ) : null}
                      <Td className="text-right">
                        <span className={
                          !ratio ? '' :
                          ratio < 0.95 ? 'text-emerald-600 font-medium' :
                          ratio > 1.10 ? 'text-red-600 font-medium' :
                          ''
                        }>
                          {fmtNum(entry.avg_l_per_100km)}
                        </span>
                        {ratio ? (
                          <span className="text-xs text-muted-foreground ml-1">
                            {ratio < 1 ? <TrendingDown className="inline h-3 w-3 text-emerald-600" /> : ratio > 1 ? <TrendingUp className="inline h-3 w-3 text-red-600" /> : null}
                          </span>
                        ) : null}
                      </Td>
                      <Td className="text-right">{fmtSar(entry.total_sar)}</Td>
                      <Td className="text-right">{fmtNum(entry.total_liters, 0)}</Td>
                      <Td className="text-right">{entry.fill_count}</Td>
                      <Td className="text-right text-xs text-muted-foreground">
                        {subject === 'drivers' ? entry.distinct_vehicles : entry.distinct_drivers ?? '—'}
                      </Td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </main>
    </div>
  );
}

function FleetStat({ label, value, unit }: { label: string; value: string; unit?: string }) {
  return (
    <div>
      <div className="text-xs text-muted-foreground uppercase">{label}</div>
      <div className="text-lg font-semibold">{value}{unit ? <span className="text-xs text-muted-foreground ml-1">{unit}</span> : null}</div>
    </div>
  );
}

function PodiumCard({ rank, entry, subject, metric }: {
  rank: number; entry: any; subject: Subject; metric: Metric;
}) {
  const { t } = useTranslation();
  const ringColor =
    rank === 1 ? 'border-amber-400 bg-amber-50 dark:bg-amber-500/10' :
    rank === 2 ? 'border-slate-300 bg-slate-50 dark:bg-slate-500/10' :
    rank === 3 ? 'border-orange-400 bg-orange-50 dark:bg-orange-500/10' :
                 'border-border bg-card';
  const icon =
    rank === 1 ? <Trophy className="h-4 w-4 text-amber-500" /> :
    rank === 2 ? <Medal className="h-4 w-4 text-slate-500" /> :
    rank === 3 ? <Award className="h-4 w-4 text-orange-500" /> :
    null;

  const value = metric === 'l_per_100km'   ? `${Number(entry.avg_l_per_100km ?? 0).toFixed(1)} L/100km`
              : metric === 'sar_per_month' ? `${Number(entry.total_sar ?? 0).toLocaleString()} SAR`
              : metric === 'sar_per_trip'  ? `${(Number(entry.total_sar ?? 0) / Math.max(1, entry.fill_count)).toFixed(0)} SAR/fill`
              :                              `${entry.fill_count} fills`;

  return (
    <div className={`rounded-md border p-3 ${ringColor}`}>
      <div className="flex items-center gap-1 text-xs text-muted-foreground">
        {icon}
        <span className="font-mono">#{rank}</span>
      </div>
      <div className="mt-1 font-medium text-sm leading-tight">
        {entry.full_name_en ?? entry.plate_en ?? '—'}
      </div>
      <div className="text-xs text-muted-foreground">
        {entry.driver_code ?? entry.vehicle_code ?? ''}
      </div>
      <div className="mt-2 text-base font-semibold">{value}</div>
    </div>
  );
}

function Th({ children, className }: { children: any; className?: string }) {
  return <th className={`px-3 py-2 text-left font-medium text-muted-foreground ${className ?? ''}`}>{children}</th>;
}
function Td({ children, className }: { children: any; className?: string }) {
  return <td className={`px-3 py-2 ${className ?? ''}`}>{children}</td>;
}
